/**
 * Supplies (docs/majik/DATA-ARCHITECTURE-SPEC.md → Supplies): the products
 * and services Majik sells or leaves at a site, with stock for what it keeps
 * — stock never goes below 0 — and orders: a delivery to one location, whose
 * lines are allocated (queued for the client's next invoice) once it is
 * delivered. Pure.
 */
import { z } from 'zod'
import { taxCodes } from '../clients/client.ts'
import { recordIdSchema } from '../shared/record-id.ts'

export const productTypes = ['service', 'inventory', 'non_inventory'] as const
export type ProductType = (typeof productTypes)[number]
export const productCategories = [
  'frequently_ordered',
  'special_order',
  'product',
  'service',
] as const
export const productStatuses = ['active', 'inactive'] as const

export const productTypeLabels: Record<ProductType, string> = {
  service: 'Service',
  inventory: 'Supply',
  non_inventory: 'Equipment',
}

export const productSchema = z.object({
  id: recordIdSchema,
  name: z.string().trim().min(1, 'Name it.').max(200),
  description: z.string().max(500).default(''),
  category: z.enum(productCategories),
  type: z.enum(productTypes),
  /** Null for something priced per order. */
  unitPrice: z.number().min(0).nullable().default(null),
  unitCost: z.number().min(0).nullable().default(null),
  /** Kept only for `inventory`. Never below 0. */
  stock: z.number().int().min(0).nullable().default(null),
  taxCode: z.enum(taxCodes).default('tax'),
  status: z.enum(productStatuses),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type Product = z.infer<typeof productSchema>

export const orderStatuses = [
  'draft',
  'ready_for_delivery',
  'out_for_delivery',
  'delivered',
  'allocated',
] as const
export type OrderStatus = (typeof orderStatuses)[number]

export const orderStatusLabels: Record<OrderStatus, string> = {
  draft: 'Draft',
  ready_for_delivery: 'Ready for delivery',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  allocated: 'Delivered · allocated',
}

export const orderLineSchema = z.object({
  id: recordIdSchema,
  /** Null only on an imported line that named no product; the form still asks for one (D-037). */
  productId: recordIdSchema.nullable(),
  /** Copied from the product when added. */
  itemName: z.string().min(1),
  description: z.string().default(''),
  unitPrice: z.number().min(0),
  quantity: z.number().positive(),
  /** When it became a charge waiting for the client's next invoice. */
  allocatedAt: z.iso.datetime().nullable().default(null),
  sortOrder: z.number().int(),
})
export type OrderLine = z.infer<typeof orderLineSchema>

export const orderSchema = z.object({
  id: recordIdSchema,
  /** Defaults to the location's name. */
  name: z.string().min(1).max(200),
  locationId: recordIdSchema,
  status: z.enum(orderStatuses),
  /** The day it was ordered ('YYYY-MM-DD', business zone); a new one is dated the day it is made. */
  orderedOn: z.iso.date(),
  deliveredOn: z.iso.date().nullable().default(null),
  /** Free text, e.g. "Arista on 03/19". */
  acceptedBy: z.string().max(120).default(''),
  notes: z.string().max(2000).default(''),
  lines: z.array(orderLineSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type Order = z.infer<typeof orderSchema>

export function orderTotal(order: Pick<Order, 'lines'>): number {
  return Math.round(order.lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0) * 100) / 100
}

/** Stock after a delivery: what was kept less what went out, never below 0. */
export function stockAfter(stock: number | null, delivered: number): number | null {
  return stock === null ? null : Math.max(0, stock - delivered)
}
